/**
 * Frontend Logger Service
 *
 * Features:
 * - Logs to console (development)
 * - Saves logs to localStorage
 * - Export logs to file
 * - Automatic log rotation (max 1000 entries)
 * - Log levels: DEBUG, INFO, WARN, ERROR
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
}

const LOG_STORAGE_KEY = 'barberline_logs';
const MAX_LOG_ENTRIES = 1000;
const IS_DEV = import.meta.env.DEV;

class Logger {
  /**
   * Get all logs from localStorage
   */
  private getLogs(): LogEntry[] {
    try {
      const logs = localStorage.getItem(LOG_STORAGE_KEY);
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }

  /**
   * Save logs to localStorage
   */
  private saveLogs(logs: LogEntry[]): void {
    try {
      // Limit to MAX_LOG_ENTRIES (oldest entries removed)
      if (logs.length > MAX_LOG_ENTRIES) {
        logs = logs.slice(-MAX_LOG_ENTRIES);
      }
      localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to save logs:', error);
    }
  }

  /**
   * Add log entry
   */
  private log(level: LogLevel, message: string, data?: any): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    };

    // Save to localStorage
    const logs = this.getLogs();
    logs.push(entry);
    this.saveLogs(logs);

    // Output to console (with colors - bright and high contrast)
    const colors = {
      DEBUG: 'color: #ffffff; background: #6c757d; padding: 2px 6px; border-radius: 3px',
      INFO: 'color: #000000; background: #00ff00; padding: 2px 6px; border-radius: 3px',
      WARN: 'color: #000000; background: #ffff00; padding: 2px 6px; border-radius: 3px',
      ERROR: 'color: #ffffff; background: #ff0000; padding: 2px 6px; border-radius: 3px',
    };

    const logFn = level === LogLevel.ERROR ? console.error :
                  level === LogLevel.WARN ? console.warn : console.log;

    logFn(
      `%c[${level}]%c ${entry.timestamp} %c${message}`,
      `${colors[level]}; font-weight: bold`,
      'color: #999999; font-size: 0.9em',
      'color: #000000; font-weight: bold',
      data || ''
    );
  }

  /**
   * DEBUG level log
   */
  debug(message: string, data?: any): void {
    if (IS_DEV) {
      this.log(LogLevel.DEBUG, message, data);
    }
  }

  /**
   * INFO level log
   */
  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }

  /**
   * WARN level log
   */
  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }

  /**
   * ERROR level log
   */
  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, message, data);
  }

  /**
   * Get all logs
   */
  getAllLogs(): LogEntry[] {
    return this.getLogs();
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    localStorage.removeItem(LOG_STORAGE_KEY);
    console.log('✅ All logs cleared');
  }

  /**
   * Export logs to file
   */
  exportLogs(): void {
    const logs = this.getLogs();

    if (logs.length === 0) {
      alert('Log\'lar yo\'q');
      return;
    }

    // Format logs as text
    const logText = logs
      .map((log) => {
        const dataStr = log.data ? `\n    Data: ${JSON.stringify(log.data, null, 2)}` : '';
        return `[${log.level}] ${log.timestamp}\n  ${log.message}${dataStr}`;
      })
      .join('\n\n');

    // Create download
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `barberline-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Logs exported: ${logs.length} entries`);
  }

  /**
   * Export logs as JSON
   */
  exportLogsJSON(): void {
    const logs = this.getLogs();

    if (logs.length === 0) {
      alert('Log\'lar yo\'q');
      return;
    }

    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `barberline-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Logs exported as JSON: ${logs.length} entries`);
  }

  /**
   * Get log statistics
   */
  getStats(): { total: number; byLevel: Record<LogLevel, number> } {
    const logs = this.getLogs();
    const byLevel = {
      [LogLevel.DEBUG]: 0,
      [LogLevel.INFO]: 0,
      [LogLevel.WARN]: 0,
      [LogLevel.ERROR]: 0,
    };

    logs.forEach((log) => {
      byLevel[log.level]++;
    });

    return {
      total: logs.length,
      byLevel,
    };
  }
}

// Export singleton instance
export const logger = new Logger();

// Global error handler
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Global Error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Only log safe metadata — never log raw reason which may contain sensitive API payloads
    const reason = event.reason;
    const safeInfo: Record<string, unknown> = {};
    if (reason instanceof Error) {
      safeInfo.message = reason.message;
      safeInfo.name = reason.name;
    } else if (reason && typeof reason === 'object') {
      const r = reason as Record<string, unknown>;
      if (r.status) safeInfo.status = r.status;
      if (r.code) safeInfo.code = r.code;
    }
    logger.error('Unhandled Promise Rejection', safeInfo);
  });
}
